<template>
  <!-- GEO_EXEC_R18. The switch off (404) leaves nothing behind: no heading, no empty state. -->
  <section v-if="show" class="pl-sec rec-sec" aria-labelledby="plan-recos-heading">
    <h2 id="plan-recos-heading" class="pl-h">Recommendations</h2>
    <p class="rec-sub">One at a time, each one tied to a number in your own report.</p>

    <p v-if="error" class="rec-sec-error" role="alert">
      {{ error }} <button type="button" class="atbtn" @click="load">Try again</button>
    </p>

    <RecommendationCard
      v-for="row in rows"
      :key="row.id"
      :row="row"
      :emphasis="row.id === topId"
      @changed="onChanged"
      @gone="rows = []"
    />

    <template v-if="skipped.length">
      <button type="button" class="rec-fold" :aria-expanded="showSkipped ? 'true' : 'false'" @click="showSkipped = !showSkipped">
        {{ showSkipped ? "Hide" : "Show" }} skipped ({{ skipped.length }})
      </button>
      <RecommendationCard v-for="row in (showSkipped ? skipped : [])" :key="`skipped-${row.id}`" :row="row" compact />
    </template>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { getRecommendations } from "../api/geoExec";
import { skippedRows, topRowId, visibleRows } from "../composables/recommendations";
import { currentBrand } from "../stores/brands";
import { featureHidden, noteFeature } from "../stores/geoExecFeatures";
import RecommendationCard from "./RecommendationCard.vue";

const all = ref([]);
const rows = ref([]);
const error = ref("");
const showSkipped = ref(false);
let controller;

const skipped = computed(() => skippedRows(all.value));
const topId = computed(() => topRowId(all.value));
const show = computed(() => !featureHidden("recommendations") && (rows.value.length > 0 || skipped.value.length > 0 || Boolean(error.value)));

async function load() {
  const brandId = currentBrand.value?.id;
  if (!brandId || featureHidden("recommendations")) {
    all.value = [];
    rows.value = [];
    return;
  }
  controller?.abort();
  controller = new AbortController();
  error.value = "";
  try {
    const payload = await getRecommendations(brandId, { signal: controller.signal });
    noteFeature("recommendations", null);
    all.value = Array.isArray(payload?.recommendations) ? payload.recommendations : [];
    rows.value = visibleRows(all.value);
  } catch (caught) {
    if (caught.name === "AbortError") return;
    if (noteFeature("recommendations", caught)) {
      all.value = [];
      rows.value = [];
      return; // the switch is off: this whole section is not part of the page
    }
    // A brand with no recommendations yet is not an error worth a red line on the plan.
    if (caught.status === 404) {
      all.value = [];
      rows.value = [];
      return;
    }
    error.value = caught.message || "Recommendations are unavailable right now.";
  }
}

function onChanged() {
  void load();
}

onMounted(load);
watch(() => currentBrand.value?.id, load);
onBeforeUnmount(() => controller?.abort());
</script>

<style scoped>
.rec-sec {
  margin-top: 8px;
}

.rec-sub {
  margin: 0 0 12px;
  color: var(--mute);
  font-size: var(--fs-caption);
}

.rec-sec-error {
  margin: 0 0 12px;
  color: var(--verm);
  font-size: var(--fs-caption);
}

.rec-fold {
  margin: 4px 0 10px;
  padding: 6px 0;
  min-height: 36px;
  border: 0;
  background: none;
  color: var(--indigo);
  font: inherit;
  font-size: var(--fs-caption);
  font-weight: 650;
  cursor: pointer;
}

.rec-fold:hover {
  text-decoration: underline;
}
</style>
